import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Medal, Award, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const Leaderboard = ({ user, onLogout }) => {
  const [rankings, setRankings] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRanking();
  }, []);

  const fetchRanking = async () => {
    try {
      setLoading(true);
      const [dailyRes, monthlyRes] = await Promise.all([
        apiClient.get('/ranking/daily-top10'),
        apiClient.get('/ranking/monthly-top10'),
      ]);
      setRankings(dailyRes.data);
      setMonthly(monthlyRes.data);
    } catch (error) {
      toast.error('Error al cargar el ranking');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (pos) => {
    switch (pos) {
      case 1: return <Crown size={20} className="text-black" />;
      case 2: return <Medal size={20} className="text-gray-500" />;
      case 3: return <Award size={20} className="text-gray-400" />;
      default: return <span className="text-sm font-bold text-black">#{pos}</span>;
    }
  };

  const renderList = (entries, testidPrefix, emptyText) => {
    if (!entries || entries.length === 0) {
      return (
        <div className="text-center py-10" data-testid={`${testidPrefix}-empty`}>
          <p className="text-gray-600">{emptyText}</p>
        </div>
      );
    }
    return (
      <div className="space-y-3" data-testid={`${testidPrefix}-list`}>
        {entries.map((entry, index) => {
          const pos = index + 1;
          const isCurrentUser = entry.user_id === user.id;
          return (
            <div
              key={entry.user_id}
              data-testid={`${testidPrefix}-entry-${pos}`}
              className={`p-4 rounded-lg flex items-center justify-between border ${isCurrentUser ? 'border-black border-2' : 'border-gray-300'}`}
            >
              <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0">
                  {getRankIcon(pos)}
                </div>
                <div className="w-10 h-10 rounded-full border-2 border-black flex items-center justify-center flex-shrink-0 overflow-hidden bg-white">
                  {entry.profile_image ? (
                    <img src={entry.profile_image} alt={entry.full_name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-sm font-bold text-black">{entry.full_name.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm sm:text-base font-semibold text-black truncate">{entry.full_name}</h3>
                    {isCurrentUser && (
                      <span className="bg-gray-200 text-black px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0">Tú</span>
                    )}
                  </div>
                  {entry.rank_name && (
                    <p className="text-[11px] sm:text-xs font-medium text-gray-600">{entry.rank_name}</p>
                  )}
                  <p className="text-[10px] sm:text-xs text-gray-500">
                    {[
                      entry.quiz_count > 0 && `${entry.quiz_count} cuest.`,
                      entry.simulacro_count > 0 && `${entry.simulacro_count} sim.`,
                      entry.escape_room_count > 0 && `${entry.escape_room_count} esc.`,
                      entry.duel_win_count > 0 && `${entry.duel_win_count} duelos`,
                      entry.imagendx_count > 0 && `${entry.imagendx_count} img.`,
                    ].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0 ml-2">
                <div className="text-lg sm:text-xl font-bold text-black">{entry.score.toLocaleString()}</div>
                <div className="text-[10px] sm:text-xs text-gray-500">pts</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" data-testid="back-to-dashboard" className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Menú Principal</span>
          </Link>
          <button onClick={onLogout} className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all">
            <span className="text-black text-sm px-2">Salir</span>
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
        <div className="text-center mb-8">
          <h1 data-testid="ranking-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black">
            Ranking del Día
          </h1>
          <p className="text-xs text-gray-400 mt-1">Se reinicia cada día a las 9:00 PM</p>
        </div>

        {/* Formula legend */}
        <div className="mb-6 text-center">
          <div className="inline-flex flex-wrap justify-center gap-x-3 gap-y-1 text-[10px] sm:text-xs text-gray-500">
            <span>Cuestionario <b>×25</b></span>
            <span>Simulacro <b>×50</b></span>
            <span>Juego de Cartas <b>×10</b></span>
            <span>Duelo ganado <b>×10</b></span>
            <span>Duelo empate <b>×5</b></span>
            <span>Escape Room <b>×8</b></span>
            <span>Imagen Dx <b>×2</b></span>
          </div>
        </div>

        {rankings.length === 0 ? (
          <div className="text-center py-12" data-testid="ranking-empty">
            <h3 className="text-xl font-semibold text-black mb-2">Sin actividad hoy</h3>
            <p className="text-gray-600 mb-4">Aún nadie ha sumado puntos en este periodo. ¡Sé el primero!</p>
            <Link to="/" data-testid="start-quiz-link" className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-block">
              Comenzar
            </Link>
          </div>
        ) : (
          renderList(rankings, 'ranking', 'Sin actividad hoy.')
        )}

        {/* Monthly Ranking */}
        <div className="mt-16">
          <div className="text-center mb-6">
            <h2 data-testid="monthly-ranking-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black">
              Ranking Mensual
            </h2>
            <p className="text-xs text-gray-400 mt-1">Puntaje acumulado en los últimos 30 días · Top 10</p>
          </div>
          {renderList(monthly, 'monthly-ranking', 'Aún nadie ha sumado puntos en los últimos 30 días.')}
        </div>

        <div className="mt-8 text-center space-x-4">
          <Link to="/" data-testid="practice-more-button" className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-block">
            Menú Principal
          </Link>
          <button onClick={fetchRanking} data-testid="refresh-ranking-button" className="px-6 py-2 bg-white text-black border border-black rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center gap-2">
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
